import { useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { FormField, FormHelp, Label } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyFloorPlanEditWebApiHandler_dateAvailable } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import { useWizardNavigation } from '../../CreateListingPage/WizardNavigationProvider';
import DatePickerWithDateSelect from '../../DatePickerWithDateSelect';

const inputName = 'dateAvailable';

const DateAvailableFormField = ({
  isRelistingFieldset = false,
  showNextMonthButton = true,
}: {
  isRelistingFieldset?: boolean;
  showNextMonthButton?: boolean;
}) => {
  const router = useRouter();
  const formModeContext = useFormMode();
  const navigation = useWizardNavigation();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const floorPlans = listingDetails?.floorPlans || [];
  const floorPlanId = floorPlans[0]?.id;
  const initialDateAvailable = floorPlans[0]?.dateAvailable ?? '';

  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  const formatDisplayDate = (isoDate: string) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return isValid(date) ? format(date, 'MM/dd/yyyy') : '';
  };

  // Convert MM/DD/YYYY to YYYY-MM-DD for backend
  const formatStorageDate = (displayDate: string) => {
    if (!displayDate) return '';
    const date = new Date(displayDate);
    return isValid(date) ? format(date, 'yyyy-MM-dd') : '';
  };

  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>(null);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_dateAvailable,
  });

  const dateField = useFieldSave({
    fieldName: inputName,
    initialValue: formatDisplayDate(initialDateAvailable),
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      getParams: { floorPlanId: floorPlanId!, dateAvailable: formatStorageDate(value) },
    }),
  });

  const handleDateChange = (newValue: string | Date) => {
    const formatted = typeof newValue === 'string' ? newValue : format(newValue, 'MM/dd/yyyy');
    dateField.onChange(formatted);
    setCustomErrorMessage(null);
  };

  const handleBlur = () => {
    // Validate MM/DD/YYYY format
    if (dateField.value && !isValid(new Date(dateField.value))) {
      setCustomErrorMessage('Date must be in MM/DD/YYYY format');
      if (formModeContext.mode === 'wizard') {
        navigation.reportError();
      }
      return;
    } else {
      navigation.clearError();
    }

    dateField.onBlur?.();
  };

  const today = new Date();
  const startOfYesterday = new Date(today.getTime() - 24 * 3600 * 1000);
  const oneYearFromNow = new Date(startOfYesterday.getTime());
  oneYearFromNow.setFullYear(startOfYesterday.getFullYear() + 1);

  return (
    <FormField
      data-testid="DateAvailableFormField"
      label={<Label size={isRelistingFieldset ? 'lg' : 'md'}>Date available</Label>}
      error={!!dateField.error || !!customErrorMessage}
      description={<FormHelp>{dateField.error || customErrorMessage}</FormHelp>}
    >
      <DatePickerWithDateSelect
        inputName={inputName}
        onInputBlur={handleBlur}
        onDateChange={handleDateChange}
        initialDate={initialDateAvailable}
        showNextMonthButton={showNextMonthButton}
        setMaxDateOneYearAhead={true}
      />
    </FormField>
  );
};

export default DateAvailableFormField;
